import { FC, SetStateAction, SVGProps, useCallback, useEffect, useMemo, useState } from 'react'
import { debounce } from 'lodash'
import { ReactComponent as EnterIconActive } from '@interface-images/enter-icon-active.svg'
import { ReactComponent as EnterIcon } from '@interface-images/enter-icon.svg'
import { ReactComponent as SearchIcon } from '@interface-images/search.svg'
import useMouseOverOut from '@hooks/useMouseOverOut'
import Input, { InputProps } from './Input'
import { EnterButtonWrapper } from './styled'

const ICON_SIZE = 28
const SEARCH_DEBOUNCE_MS = 400

type Props = {
  onSearch?: (value: string) => void
  value?: string
}

const RightIconButton: FC<
  SVGProps<SVGSVGElement> & { title?: string | undefined }
> = () => {
  const [hovered, , handleMouseEnter, handleMouseLeave] = useMouseOverOut()

  return (
    <EnterButtonWrapper
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {hovered ? (
        <EnterIconActive height={ICON_SIZE} width={ICON_SIZE} />
      ) : (
        <EnterIcon height={ICON_SIZE} width={ICON_SIZE} />
      )}
    </EnterButtonWrapper>
  )
}

const SearchInput: FC<Props & InputProps> = ({ onSearch, ...rest }) => {
  const [value, setValue] = useState<string | undefined>()

  const debouncedSearch = useMemo(
    () =>
      debounce((searchValue: string) => {
        onSearch?.(searchValue)
      }, SEARCH_DEBOUNCE_MS),
    [onSearch]
  )

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch])

  const handleSearch = useCallback(() => {
    debouncedSearch.cancel()
    onSearch?.(value as string)
  }, [debouncedSearch, onSearch, value])

  const handleChange = useCallback(
    (e: { target: { value: SetStateAction<string> } }) => {
      const newValue = e.target.value as string
      setValue(newValue)
      debouncedSearch(newValue)
    },
    [debouncedSearch]
  )

  const handleKeyDown = useCallback(
    (e: { key: string }) => {
      if (e.key === 'Enter') {
        handleSearch()
      }
    },
    [handleSearch]
  )

  return (
    <Input
      {...rest}
      onKeyDown={handleKeyDown}
      onChange={handleChange}
      value={value}
      LeftIcon={SearchIcon}
      RightIcon={RightIconButton}
      rightIconProps={{
        onClick: handleSearch,
      }}
    />
  )
}

export default SearchInput
